import { createClient } from "@supabase/supabase-js";

// ─── Client ───────────────────────────────────────────────────────────────────
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || "https://shhpbhjptrxejdruiwhr.supabase.co";
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNoaHBiaGpwdHJ4ZWpkcnVpd2hyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzA4NzY1MzAsImV4cCI6MjA4NjQ1MjUzMH0.TLEzIIlh9EG_CwlqoPvdPuuLa0viLohYvjl53eKt7Bk";

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: { persistSession: true, autoRefreshToken: true },
});

// ─── TABLE NAME ───────────────────────────────────────────────────────────────
const TABLE = "waitlist_users";

// ─────────────────────────────────────────────────────────────────────────────
//  WAITLIST FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * submitToWaitlist
 * Inserts a new user into the waitlist_users table.
 * Returns { data, error, isDuplicate }
 *
 * @param {{ email: string, firstName?: string }} payload
 */
export async function submitToWaitlist({ email, firstName = null }) {
  const { data, error } = await supabase
    .from(TABLE)
    .insert({
      email: email.toLowerCase().trim(),
      first_name: firstName?.trim() || null,
      source: "waitlist_page",
      status: "waitlist",
    })
    .select()
    .single();

  const isDuplicate =
    error?.code === "23505" ||
    error?.message?.toLowerCase().includes("duplicate") ||
    error?.message?.toLowerCase().includes("unique");

  return { data, error, isDuplicate };
}

/**
 * checkEmailExists
 * Returns true if the email already exists in the waitlist.
 *
 * @param {string} email
 */
export async function checkEmailExists(email) {
  const { count, error } = await supabase
    .from(TABLE)
    .select("id", { count: "exact", head: true })
    .eq("email", email.toLowerCase().trim());

  if (error) return false;
  return count > 0;
}

// ─────────────────────────────────────────────────────────────────────────────
//  ADMIN — READ FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * getAllWaitlistUsers
 * Fetches all users ordered by signup date descending.
 * Supports optional search filter (email or first_name).
 *
 * @param {{ search?: string, limit?: number, offset?: number }} opts
 */
export async function getAllWaitlistUsers({ search = "", limit = 100, offset = 0 } = {}) {
  let query = supabase
    .from(TABLE)
    .select("id, email, first_name, status, source, created_at", { count: "exact" })
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1);

  if (search.trim()) {
    query = query.or(
      `email.ilike.%${search.trim()}%,first_name.ilike.%${search.trim()}%`
    );
  }

  const { data, error, count } = await query;
  return { data: data || [], error, total: count || 0 };
}

/**
 * getWaitlistStats
 * Returns total, today, thisWeek, thisMonth signup counts.
 */
export async function getWaitlistStats() {
  const now = new Date();

  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);

  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - 7);

  const monthStart = new Date(now);
  monthStart.setDate(1);
  monthStart.setHours(0, 0, 0, 0);

  const [totalRes, todayRes, weekRes, monthRes] = await Promise.all([
    supabase.from(TABLE).select("id", { count: "exact", head: true }),
    supabase.from(TABLE).select("id", { count: "exact", head: true }).gte("created_at", todayStart.toISOString()),
    supabase.from(TABLE).select("id", { count: "exact", head: true }).gte("created_at", weekStart.toISOString()),
    supabase.from(TABLE).select("id", { count: "exact", head: true }).gte("created_at", monthStart.toISOString()),
  ]);

  return {
    total: totalRes.count || 0,
    today: todayRes.count || 0,
    thisWeek: weekRes.count || 0,
    thisMonth: monthRes.count || 0,
    error: totalRes.error || todayRes.error || weekRes.error || monthRes.error,
  };
}

/**
 * getRecentSignups
 * Returns the last N signups for the activity feed.
 *
 * @param {number} limit
 */
export async function getRecentSignups(limit = 5) {
  const { data, error } = await supabase
    .from(TABLE)
    .select("id, email, first_name, created_at")
    .order("created_at", { ascending: false })
    .limit(limit);

  return { data: data || [], error };
}

/**
 * getSignupsByDay
 * Returns daily signup counts for the last N days (for chart).
 *
 * @param {number} days
 */
export async function getSignupsByDay(days = 14) {
  const since = new Date();
  since.setDate(since.getDate() - days);

  const { data, error } = await supabase
    .from(TABLE)
    .select("created_at")
    .gte("created_at", since.toISOString())
    .order("created_at", { ascending: true });

  if (error) return { data: [], error };

  // Group by day
  const counts = {};
  for (let i = 0; i < days; i++) {
    const d = new Date();
    d.setDate(d.getDate() - (days - 1 - i));
    const key = d.toISOString().split("T")[0];
    counts[key] = 0;
  }
  (data || []).forEach((row) => {
    const key = row.created_at.split("T")[0];
    if (counts[key] !== undefined) counts[key]++;
  });

  return {
    data: Object.entries(counts).map(([date, count]) => ({ date, count })),
    error: null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
//  ADMIN — WRITE FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * updateUserStatus
 * Updates the status of a waitlist user.
 *
 * @param {string} id
 * @param {string} status  e.g. "waitlist" | "invited" | "active" | "rejected"
 */
export async function updateUserStatus(id, status) {
  const { data, error } = await supabase
    .from(TABLE)
    .update({ status })
    .eq("id", id)
    .select()
    .single();

  return { data, error };
}

/**
 * deleteUser
 * Permanently removes a user from the waitlist.
 *
 * @param {string} id
 */
export async function deleteUser(id) {
  const { data, error } = await supabase
    .from(TABLE)
    .delete()
    .eq("id", id)
    .select();

  if (error) {
    console.error("Delete error:", error);
  }

  return { data, error };
}

/**
 * bulkUpdateStatus
 * Updates status for multiple users at once.
 *
 * @param {string[]} ids
 * @param {string} status
 */
export async function bulkUpdateStatus(ids, status) {
  const { data, error } = await supabase
    .from(TABLE)
    .update({ status })
    .in("id", ids)
    .select();

  return { data, error };
}

/**
 * bulkDeleteUsers
 * Deletes multiple users at once.
 *
 * @param {string[]} ids
 */
export async function bulkDeleteUsers(ids) {
  const { error } = await supabase.from(TABLE).delete().in("id", ids);
  return { error };
}

// ─────────────────────────────────────────────────────────────────────────────
//  REALTIME SUBSCRIPTION
// ─────────────────────────────────────────────────────────────────────────────

/**
 * subscribeToWaitlist
 * Sets up a realtime listener for new waitlist inserts.
 * Returns an unsubscribe function.
 *
 * @param {(payload: any) => void} onInsert
 * @param {(payload: any) => void} onDelete
 */
export function subscribeToWaitlist(onInsert, onDelete) {
  const channel = supabase
    .channel("waitlist-changes")
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: TABLE },
      (payload) => onInsert && onInsert(payload.new)
    )
    .on(
      "postgres_changes",
      { event: "DELETE", schema: "public", table: TABLE },
      (payload) => onDelete && onDelete(payload.old)
    )
    .subscribe();

  return () => supabase.removeChannel(channel);
}

// ─────────────────────────────────────────────────────────────────────────────
//  EXPORT HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * exportToCSV
 * Converts user array to CSV and triggers browser download.
 *
 * @param {any[]} users
 */
export function exportToCSV(users) {
  const headers = ["#", "Email", "First Name", "Status", "Source", "Joined At"];
  const rows = users.map((u, i) => [
    i + 1,
    u.email,
    u.first_name || "",
    u.status || "waitlist",
    u.source || "waitlist_page",
    new Date(u.created_at).toISOString(),
  ]);

  const csv = [headers, ...rows]
    .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `alphastack_waitlist_${new Date().toISOString().split("T")[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * copyEmailsToClipboard
 * Copies all emails (newline-separated) to clipboard.
 *
 * @param {any[]} users
 */
export async function copyEmailsToClipboard(users) {
  const emails = users.map((u) => u.email).join("\n");
  await navigator.clipboard.writeText(emails);
}

// ─────────────────────────────────────────────────────────────────────────────
//  VALIDATION
// ─────────────────────────────────────────────────────────────────────────────

export const validateEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

export const STATUS_OPTIONS = [
  { value: "waitlist", label: "Waitlist", color: "#C9A84C" },
  { value: "invited", label: "Invited", color: "#60A5FA" },
  { value: "active", label: "Active", color: "#4ADE80" },
  { value: "rejected", label: "Rejected", color: "#F87171" },
];
